import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { routeTree } from "./routeTree.gen";
import "./styles/index.css";
import { appNavigationHistory, currentHistoryEntry } from "@/lib/app-navigation-history";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: false, refetchOnWindowFocus: false, staleTime: 5_000 },
  },
});

// The SPA is served at `/` in both dev (Vite :5174) and prod (Fastify
// at :3000 serving dist/web), so basepath is always "/". Computed
// from Vite's BASE_URL global anyway in case we ever need to subpath.
const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  basepath: import.meta.env.BASE_URL.replace(/\/$/, "") || "/",
});

// Only resolved in-app navigations become trusted history entries.
router.subscribe("onResolved", () => {
  const entry = currentHistoryEntry();
  if (entry) appNavigationHistory.observe(entry.key, entry.index);
});

// The entry document is not evidence of a prior route in this app.
const initialEntry = currentHistoryEntry();
if (initialEntry) appNavigationHistory.observe(initialEntry.key, initialEntry.index);

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
